import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Ticket, 
  Eye, 
  ShoppingCart,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  Percent
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple bar chart component (no external dependencies)
const SimpleBarChart = ({ data, label, color = '#4A7C59' }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <div className="flex items-end gap-1 h-32">
        {data.map((item, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(item.value / maxValue) * 100}%` }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="w-full rounded-t-sm min-h-[4px]"
              style={{ backgroundColor: color }}
              title={`${item.label}: ${item.value}`}
            />
            <span className="text-[10px] text-muted-foreground truncate w-full text-center">
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Donut chart component
const SimpleDonutChart = ({ data, size = 120 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  let cumulative = 0;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {data.map((item, i) => {
          const percentage = (item.value / total) * 100;
          const strokeDasharray = `${percentage} ${100 - percentage}`;
          const strokeDashoffset = -cumulative;
          cumulative += percentage;
          
          return (
            <circle
              key={i}
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={item.color}
              strokeWidth="20"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold">{total}</span>
      </div>
    </div>
  );
};

// Stat card component
const StatCard = ({ title, value, change, changeLabel, icon: Icon, color = 'emerald' }) => {
  const isPositive = change >= 0;
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
    amber: 'bg-amber-500/10 text-amber-500'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl p-5 border border-border"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{isPositive ? '+' : ''}{change}%</span>
              <span className="text-muted-foreground">{changeLabel}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </motion.div>
  );
};

export function AnalyticsDashboard({ businessId, plan = 'basic', onUpgrade }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // week, month, year
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [businessId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/analytics/business/${businessId}`, {
        params: { period },
        headers: { 'X-Business-Id': businessId }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Mock data for demo
      setAnalytics(generateMockAnalytics(period));
    } finally {
      setLoading(false);
    }
  };

  const generateMockAnalytics = (period) => {
    const days = period === 'week' ? 7 : period === 'month' ? 30 : 365;
    const labels = period === 'week' 
      ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
      : period === 'month'
      ? Array.from({ length: 4 }, (_, i) => `Sem ${i + 1}`)
      : ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    return {
      summary: {
        totalViews: Math.floor(Math.random() * 1000) + 500,
        totalReservations: Math.floor(Math.random() * 100) + 20,
        totalRedemptions: Math.floor(Math.random() * 50) + 10,
        conversionRate: (Math.random() * 10 + 5).toFixed(1),
        avgTicketValue: (Math.random() * 30 + 15).toFixed(2)
      },
      changes: {
        views: Math.floor(Math.random() * 40) - 10,
        reservations: Math.floor(Math.random() * 30) - 5,
        redemptions: Math.floor(Math.random() * 20) - 5,
        conversion: (Math.random() * 4 - 1).toFixed(1)
      },
      viewsOverTime: labels.map(label => ({
        label,
        value: Math.floor(Math.random() * 100) + 20
      })),
      reservationsOverTime: labels.map(label => ({
        label,
        value: Math.floor(Math.random() * 20) + 2
      })),
      kuponPerformance: [
        { name: '2x1 Café', views: 234, reservations: 45, redemptions: 32 },
        { name: '-30% Menú', views: 189, reservations: 28, redemptions: 19 },
        { name: 'Happy Hour', views: 156, reservations: 22, redemptions: 15 },
        { name: 'Postre Gratis', views: 98, reservations: 12, redemptions: 8 }
      ],
      redemptionsByStatus: [
        { label: 'Canjeados', value: 67, color: '#10b981' },
        { label: 'Pendientes', value: 23, color: '#f59e0b' },
        { label: 'Expirados', value: 10, color: '#ef4444' }
      ],
      topHours: [
        { hour: '12:00', reservations: 18 },
        { hour: '13:00', reservations: 24 },
        { hour: '14:00', reservations: 31 },
        { hour: '19:00', reservations: 22 },
        { hour: '20:00', reservations: 28 },
        { hour: '21:00', reservations: 19 }
      ]
    };
  };

  const handleExport = () => {
    if (plan === 'basic') {
      toast.error('Exportar datos requiere plan Pro o superior');
      return;
    }
    
    // Generate CSV
    const csvContent = [
      ['Métrica', 'Valor'],
      ['Vistas Totales', analytics?.summary?.totalViews],
      ['Reservas Totales', analytics?.summary?.totalReservations],
      ['Canjes Totales', analytics?.summary?.totalRedemptions],
      ['Tasa de Conversión', analytics?.summary?.conversionRate + '%'],
      ['Valor Medio del Ticket', analytics?.summary?.avgTicketValue + '€']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Datos exportados correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Dashboard de Analíticas
          </h2>
          <p className="text-sm text-muted-foreground">
            Plan {plan.charAt(0).toUpperCase() + plan.slice(1)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Period Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            {[
              { id: 'week', label: '7D' },
              { id: 'month', label: '30D' },
              { id: 'year', label: '12M' }
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p.id 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={plan === 'basic'}
            data-testid="export-analytics"
          >
            <Download className="w-4 h-4 mr-1" />
            Exportar
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAnalytics}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Vistas Totales"
          value={analytics?.summary?.totalViews?.toLocaleString()}
          change={analytics?.changes?.views}
          changeLabel="vs anterior"
          icon={Eye}
          color="blue"
        />
        <StatCard
          title="Reservas"
          value={analytics?.summary?.totalReservations}
          change={analytics?.changes?.reservations}
          changeLabel="vs anterior"
          icon={ShoppingCart}
          color="emerald"
        />
        <StatCard
          title="Canjes"
          value={analytics?.summary?.totalRedemptions}
          change={analytics?.changes?.redemptions}
          changeLabel="vs anterior"
          icon={CheckCircle}
          color="purple"
        />
        <StatCard
          title="Conversión"
          value={`${analytics?.summary?.conversionRate}%`}
          change={parseFloat(analytics?.changes?.conversion)}
          changeLabel="vs anterior"
          icon={Percent}
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Chart */}
        <div className="bg-card rounded-xl p-5 border border-border">
          <SimpleBarChart
            data={analytics?.viewsOverTime || []}
            label="Vistas por Período"
            color="#3b82f6"
          />
        </div>

        {/* Reservations Chart */}
        <div className="bg-card rounded-xl p-5 border border-border">
          <SimpleBarChart
            data={analytics?.reservationsOverTime || []}
            label="Reservas por Período"
            color="#10b981"
          />
        </div>
      </div>

      {/* Pro/Enterprise Features */}
      {(plan === 'pro' || plan === 'enterprise') && (
        <>
          {/* Kupon Performance Table */}
          <div className="bg-card rounded-xl p-5 border border-border">
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Ticket className="w-4 h-4 text-primary" />
              Rendimiento por Kupon
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 font-medium">Kupon</th>
                    <th className="text-right py-2 px-3 font-medium">Vistas</th>
                    <th className="text-right py-2 px-3 font-medium">Reservas</th>
                    <th className="text-right py-2 px-3 font-medium">Canjes</th>
                    <th className="text-right py-2 px-3 font-medium">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics?.kuponPerformance?.map((kupon, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 px-3 font-medium">{kupon.name}</td>
                      <td className="text-right py-2 px-3 text-muted-foreground">{kupon.views}</td>
                      <td className="text-right py-2 px-3 text-blue-500">{kupon.reservations}</td>
                      <td className="text-right py-2 px-3 text-emerald-500">{kupon.redemptions}</td>
                      <td className="text-right py-2 px-3">
                        <span className="bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded text-xs">
                          {((kupon.reservations / kupon.views) * 100).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Status Distribution and Top Hours */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Redemption Status */}
            <div className="bg-card rounded-xl p-5 border border-border">
              <h3 className="text-sm font-medium mb-4">Estado de Reservas</h3>
              <div className="flex items-center gap-6">
                <SimpleDonutChart data={analytics?.redemptionsByStatus || []} />
                <div className="space-y-2">
                  {analytics?.redemptionsByStatus?.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm">{item.label}</span>
                      <span className="text-sm font-bold ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Hours */}
            <div className="bg-card rounded-xl p-5 border border-border">
              <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Horas Pico de Reservas
              </h3>
              <div className="space-y-2">
                {analytics?.topHours?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm font-mono w-12">{item.hour}</span>
                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(item.reservations / 35) * 100}%` }}
                        transition={{ delay: i * 0.1, duration: 0.5 }}
                        className="h-full bg-primary rounded-full"
                      />
                    </div>
                    <span className="text-sm font-bold w-8 text-right">{item.reservations}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Upgrade Prompt for Basic */}
      {plan === 'basic' && (
        <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-xl p-6 border border-primary/20">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/20 rounded-xl">
              <TrendingUp className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Desbloquea Analíticas Avanzadas</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Con el plan Pro obtienes: rendimiento por kupon, horas pico, exportación de datos y mucho más.
              </p>
              <Button 
                className="mt-4" 
                size="sm"
                onClick={onUpgrade}
                data-testid="upgrade-analytics-btn"
              >
                Actualizar a Pro
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
